import store from "@/store/store";
import { Provider, useDispatch, useSelector } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import "rc-dropdown/assets/index.css";
import "rc-menu/assets/index.css";
import "./styles/globals.css";
import "./styles/main.css";
import MainLayout from "@/layouts/layout";
import "@xterm/xterm/css/xterm.css";
import axios from "axios";
import DatabaseSyncSideEffect from "@/components/contexts/DatabaseSync";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { LiveblocksRoomWrapper } from "@/components/contexts/LiveblocksRoomWrapper";
import ErrorBoundary from "@/components/ErrorBoundary";
import MaintenanceModal from "@/components/MaintenanceModal";
import * as Sentry from "@sentry/react";
import Home from "./pages/index";
import ExtensionLogin from "./pages/extensionLogin";
import LocalLogin from "./pages/localLogin";
import ExistingProject from "./pages/project-create/existing-project";
import Editor from "./pages/editor/index";
import AccessRequests from "./pages/access-requests";
import FigmaCallback from "./pages/figma/callback";
import GithubSuccess from "./pages/GithubSuccess";
import PromoCode from "./pages/PromoCode";
import { Navigate, Route, Routes, useRoutes } from "react-router";
import usePlanInfoStore from "./store/zustand/planInfoStore";
import Loading from "./components/ui/loading";
import { UserPreferencesModal } from "@/components/UserPreferences/UserPreferencesModal";
import { useUserPreferencesStore } from "@/components/UserPreferences/userPreferencesStore";
import { useMCPServers } from "./hooks/useMCPServers";

import {
  useUser,
  useSession,
  SignedIn,
  useClerk,
  useAuth,
  ClerkProvider,
  SignIn,
  SignUp,
} from "@clerk/clerk-react";
import { dark } from "@clerk/themes";

import {
  useInitialRenderEffect,
  useIsBitbucketConnected,
  useIsGithubConnected,
  useIsGitlabConnected,
} from "./utils/utils";
import { useUserId } from "./utils/utils";
import { getUnexpiredToken } from "./utils/utils";
import { GitProvider } from "./utils/gitProviderUtils";
import { useGlobalStore } from "./store/zustand/globalStore";

if (typeof window !== "undefined") {
  // checks that we are client-side
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
    loaded: (posthog) => {
      posthog.debug(import.meta.env.VITE_LOG_POSTHOG === "true");
    },
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
      },
    },
  });

  if (window.location.hostname !== "localhost") {
    Sentry.init({
      dsn: "https://c602bcd4c514223aceb028bdc2d8ac87@o4508337261707264.ingest.us.sentry.io/4508337262690304",
      integrations: [Sentry.browserTracingIntegration()],
      // Tracing
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    });
  }

  // jam.metadata(() => {
  //   const data = sessionStorage.getItem(JAM_METADATA) || "{}";
  //   try {
  //     return JSON.stringify(data);
  //   } catch (e) {
  //     return {};
  //   }
  // });
}

function IdentityWrapper({ children }: any) {
  const { user } = useUser();
  const { isLoaded, session } = useSession();
  const fetchReferral = usePlanInfoStore((state) => state.fetchReferral);
  const loadPreferences = useUserPreferencesStore(
    (state) => state.loadPreferences,
  );
  const { fetchFeaturedMCPs } = useMCPServers();

  const initializedRef = useRef(false);
  const preferencesLoadedRef = useRef(false);
  const featuredMCPsLoadedRef = useRef(false);
  const userId = useUserId();

  const isGitlabConnected = useIsGitlabConnected();
  const isGithubConnected = useIsGithubConnected();
  const isBitbucketConnected = useIsBitbucketConnected();

  useInitialRenderEffect(() => {
    fetchReferral();
  });

  useEffect(() => {
    const manuallySetExternalId = async () => {
      // Check number of attempts
      const urlParams = new URLSearchParams(window.location.search);
      const attempts = parseInt(urlParams.get("external-id-att") || "0");

      if (attempts >= 5) {
        console.warn(
          "Maximum number of manual update attempts reached (5), stopping to prevent infinite reload",
        );
        return;
      }

      await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/clerk/webhook/manual-update-user`,
        {},
        {
          headers: {
            Authorization: `Bearer ${await getUnexpiredToken(session)}`,
          },
        },
      );

      // Increment attempt counter and reload
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("external-id-att", (attempts + 1).toString());
      window.location.href = newUrl.toString();
    };

    const verifyAuthTokens = async () => {
      const token = await getUnexpiredToken(session);
      if (!token) {
        return;
      }

      const toCheck: GitProvider[] = [];
      if (isGitlabConnected) {
        toCheck.push(GitProvider.GITLAB);
      }
      if (isGithubConnected) {
        toCheck.push(GitProvider.GITHUB);
      }
      if (isBitbucketConnected) {
        toCheck.push(GitProvider.BITBUCKET);
      }

      for (const provider of toCheck) {
        try {
          const res = await axios.get(
            `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/verify-oauth-token?provider=${provider}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (!res?.data?.success) {
            // Important note - we do _not_ want to call reauthorize here, as it will just remove the account
            // from ther user without any indication of what happened
            if (provider === GitProvider.GITHUB) {
              useGlobalStore.getState().setGithubExpired(true);
            } else if (provider === GitProvider.GITLAB) {
              useGlobalStore.getState().setGitlabExpired(true);
            } else if (provider === GitProvider.BITBUCKET) {
              useGlobalStore.getState().setBitbucketExpired(true);
            }
          }
        } catch (e) {
          console.error(
            "Error verifying OAuth token for provider",
            provider,
            e,
          );
        }
      }
    };

    const identifyUser = async () => {
      if (initializedRef.current) {
        return;
      }

      if (!isLoaded || !user || !session) {
        return;
      }

      if (!user?.externalId) {
        manuallySetExternalId();
        return;
      }

      // Asynchronously verify all OAuth tokens
      verifyAuthTokens();

      initializedRef.current = true;

      usePlanInfoStore.getState().refreshPlanInfo(session);

      const githubUser = user?.externalAccounts.find(
        (account) => account.provider === "github",
      );

      let githubUrl = "";
      if (githubUser) {
        try {
          const githubUserId = `github|${githubUser?.providerUserId}`;
          const res = await axios.get(
            `https://api.github.com/user/${githubUserId.split("|")[1]}`,
          );
          githubUrl = res.data.html_url;
        } catch (e) {
          console.error("Error fetching github user", e);
        }
      }

      // If no external ID, for debugging only, use the clerk ID specifically
      posthog.identify(userId || user?.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName,
        environment: import.meta.env.VITE_ENVIRONMENT,
        github_url: githubUrl,
      });

      // X (Twitter) conversion tracking for sign-ups
      if (typeof window !== "undefined" && (window as any).twq) {
        (window as any).twq("event", "tw-q4iwi-qtsgv", {
          conversion_id: userId || user?.id,
          email_address: user.primaryEmailAddress?.emailAddress,
        });
      }

      // Load user preferences from Supabase
      if (userId && !preferencesLoadedRef.current) {
        loadPreferences(userId);
        preferencesLoadedRef.current = true;
      }

      // Load featured MCPs from database (once per app load)
      if (!featuredMCPsLoadedRef.current) {
        fetchFeaturedMCPs();
        featuredMCPsLoadedRef.current = true;
      }
    };

    identifyUser();
  }, [user, isLoaded, session, userId, loadPreferences, fetchFeaturedMCPs]);

  if (!isLoaded || !session || !user) {
    return (
      <div className="dark w-full h-full">
        <Loading size={32} />
      </div>
    );
  }

  return <>{children}</>;
}

const RouteWrapper = ({ element: Element, ...rest }) => {
  const { redirectToSignIn } = useClerk();
  const { userId, isLoaded } = useAuth();

  if (!userId && isLoaded) {
    redirectToSignIn();
    return null;
  }

  return (
    <SignedIn>
      <Provider store={store}>
        <PostHogProvider client={posthog}>
          <IdentityWrapper>
            <LiveblocksRoomWrapper>
              <MainLayout>
                <Element {...rest} />
              </MainLayout>
              <DatabaseSyncSideEffect />
            </LiveblocksRoomWrapper>
          </IdentityWrapper>
        </PostHogProvider>
      </Provider>
    </SignedIn>
  );
};

const ClerkWrapper = ({ children }: { children: React.ReactNode }) => {
  const clerkAppearance = {
    baseTheme: dark,
    layout: {
      socialButtonsPlacement: "top" as const,
      socialButtonsVariant: "blockButton" as const,
    },
    elements: {
      socialButtons: "flex! flex-col! gap-2 w-full",
      socialButtonsBlockButton:
        "w-full h-10 flex items-center justify-center gap-2 text-sm font-medium",
      formButtonPrimary: "w-full",
      card: "shadow-lg",
    },
  };

  // Determine primary and satellite domains based on Clerk domain
  const clerkDomain = import.meta.env.VITE_CLERK_DOMAIN || "tempo.new";
  const primaryDomain = clerkDomain;
  const satelliteDomains = [
    "tempo-dev.app",
    clerkDomain === "tempo.build" ? "tempo.new" : "tempo.build",
  ];

  // Check if current domain is a satellite domain
  if (
    satelliteDomains.some((domain) => window.location.host?.endsWith(domain)) &&
    import.meta.env.VITE_ENVIRONMENT === "PROD"
  ) {
    // Satellite domain - use satellite provider
    return (
      <ClerkProvider
        publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
        isSatellite
        signInUrl={
          import.meta.env.VITE_CLERK_SIGN_IN_URL ||
          `https://app.${primaryDomain}/sign-in`
        }
        signUpUrl={
          import.meta.env.VITE_CLERK_SIGN_UP_URL ||
          `https://app.${primaryDomain}/sign-up`
        }
        domain={(url) => url.host}
        appearance={clerkAppearance}
      >
        {children}
      </ClerkProvider>
    );
  } else {
    // If the origin is the primary
    return (
      <ClerkProvider
        publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}
        allowedRedirectOrigins={[
          "https://app.tempo-dev.app",
          "https://tempo-dev.app",
          "https://app.tempo.new",
          "https://tempo.new",
          "https://app.tempo.build",
          "https://tempo.build",
          "https://reactcomponents.com",
          "https://www.reactcomponents.com",
        ]}
        signInUrl={`${window.location.origin}/sign-in`}
        signUpUrl={`${window.location.origin}/sign-up`}
        appearance={clerkAppearance}
      >
        {children}
      </ClerkProvider>
    );
  }
};

function Root() {
  const ErrorBoundaryComp = import.meta.env.DEV
    ? React.Fragment
    : ErrorBoundary;

  return (
    <ErrorBoundaryComp>
      <ClerkWrapper>
        {/* Maintenance Modal - displays on top of everything when maintenance mode is active */}
        <MaintenanceModal />
        <UserPreferencesModal />

        <Routes>
          <Route index element={<RouteWrapper element={Home} />} />
          <Route
            path="extensionLogin"
            element={<RouteWrapper element={ExtensionLogin} />}
          />
          <Route
            path="sign-in"
            element={
              <div className="dark w-screen h-screen flex justify-center items-center">
                <SignIn
                  signUpUrl="/sign-up"
                  appearance={{
                    elements: {
                      socialButtonsBlockButton:
                        "w-full h-10 flex items-center justify-center gap-2 text-lg font-medium",
                    },
                  }}
                />
              </div>
            }
          />
          <Route
            path="sign-up"
            element={
              <div className="dark w-screen h-screen flex justify-center items-center">
                <SignUp
                  signInUrl="/sign-in"
                  appearance={{
                    elements: {
                      socialButtonsBlockButton:
                        "w-full h-10 flex items-center justify-center gap-2 text-lg font-medium",
                    },
                  }}
                />
              </div>
            }
          />
          <Route
            path="localLogin"
            element={<RouteWrapper element={LocalLogin} />}
          />

          <Route path="project-create">
            <Route path="ai-new" element={<Navigate replace to="/" />} />
            <Route
              path="existing-project"
              element={<RouteWrapper element={ExistingProject} />}
            />
            <Route
              path="workflow-select"
              element={<Navigate replace to="/" />}
            />
          </Route>

          <Route path="canvases/:canvasId">
            <Route index element={<Navigate to="editor" replace />} />
            <Route path="editor" element={<RouteWrapper element={Editor} />} />
            <Route
              path="webc-editor"
              element={<RouteWrapper element={Editor} />}
            />
          </Route>

          <Route
            path="access-requests/:token"
            element={<RouteWrapper element={AccessRequests} />}
          />

          <Route
            path="figma/callback"
            element={<RouteWrapper element={FigmaCallback} />}
          />
          <Route
            path="github/success"
            element={<RouteWrapper element={GithubSuccess} />}
          />
          <Route path="promo" element={<RouteWrapper element={PromoCode} />} />
        </Routes>
      </ClerkWrapper>
    </ErrorBoundaryComp>
  );
}

export default Root;
